import * as $ from 'jquery'
import { DateTime, Duration } from 'luxon'

import { CustomComponent, DateUtil } from '../common';
import { TemporalComponent, TemporalInputElement } from '../temporal-component';

import { TimeRangeDurationState } from './time-range-duration-state';

const COMPONENT_ATTR = 'iw-time-range';
const COMPONENT_SELECTOR = `[${COMPONENT_ATTR}]`;

const ATTR_START = 'start';
const ATTR_END = 'end';
const ATTR_ADD_MINUTES = 'add-minutes';
const ATTR_SUBTRACT_MINUTES = 'subtract-minutes';
const ATTR_AUTO_ADJUST = 'auto-adjust';
const ATTR_MAX = 'max'

const DATA_VALUE = 'value';
const DATA_ORIGINAL_DURATION = 'original-duration';

const ONE_DAY = Duration.fromObject({ days: 1 });

declare global {
  interface JQuery {
    iwTimeRange(): JQuery;
  }
}

/**
 * @event TimeRangeComponent#change - Emitted when the value of the duration changes
 */

/**
 * Component controller that calculates a time range between two {@link TemporalComponent} instances, and corrects the
 * respective dates to ensure the time range is constrained to less than 24 hours.
 *
 * This component does not have a UI of its own, instead providing the {@link TimeRangeComponent.value} property and
 * firing the {@link TimeRangeComponent#event:change} event when the value changes.
 *
 * TODO: the logic in {@link onTimeInputChange} may need additional changes to work correctly with time ranges that are
 *       intended to exceed 24 hours.
 *
 * @example
 * ```
 * <div iw-time-range start="#start" end="#end" add-minutes="#overtime" subtract-minutes="#breakLength">
 *   <iw-time-input id="start" />
 *   <iw-time-input id="end" />
 *   <input type="number" id="overtime" />
 *   <input type="number" id="breakLength" />
 * </div>
 * ```
 *
 * Note: The time inputs are not required to be children of the time range, but it is recommended to do so for clarity.
 */
export class TimeRangeComponent extends CustomComponent {

  public static readonly COMPONENT_SELECTOR = COMPONENT_SELECTOR;

  public static get ID_PREFIX() { return COMPONENT_ATTR };

  public static loadPlugin(): void {
    super.loadPlugin();
    if (!$.fn.iwTimeRange) {
      $.fn.iwTimeRange = this.jQueryPlugin('TimeRangeComponent');
    }
  }

  public get startSelector() {
    return this.$el.attr(ATTR_START);
  }

  public get endSelector() {
    return this.$el.attr(ATTR_END);
  }

  public get addMinutesSelector() {
    return this.$el.attr(ATTR_ADD_MINUTES);
  }

  public get subtractMinutesSelector() {
    return this.$el.attr(ATTR_SUBTRACT_MINUTES);
  }

  public get value(): TimeRangeDurationState {
    return this.$el.data(DATA_VALUE);
  }

  public get originalDuration(): Duration {
    return this.$el.data(DATA_ORIGINAL_DURATION);
  }

  /**
   * Whether the component will automatically adjust the date of the start or end time when values are entered or
   * selected that would put the start after the end.
   */
  public get autoAdjust(): boolean {
    return this.$el.attr(ATTR_AUTO_ADJUST)?.toLocaleLowerCase() !== 'false';
  }

  public readonly $start: JQuery<TemporalInputElement>;
  public readonly $end: JQuery<TemporalInputElement>;
  public readonly $addMinutes: JQuery;
  public readonly $subtractMinutes: JQuery;

  public readonly startCtrl: TemporalComponent;
  public readonly endCtrl: TemporalComponent;

  public readonly $triggers: JQuery;

  constructor($el: JQuery) {
    super($el);

    this.$start = $(this.startSelector);
    this.$end = $(this.endSelector);
    this.$addMinutes = $(this.addMinutesSelector);
    this.$subtractMinutes = $(this.subtractMinutesSelector);

    this.startCtrl = CustomComponent.getController(this.$start);
    if (!this.startCtrl || !(this.startCtrl instanceof TemporalComponent)) {
      throw new Error(`TimeRangeComponent: expected the "start" source to be a TemporalComponent instance`);
    }

    this.endCtrl = CustomComponent.getController(this.$end);
    if (!this.endCtrl || !(this.endCtrl instanceof TemporalComponent)) {
      throw new Error(`TimeRangeComponent: expected the "end" source to be a TemporalComponent instance`);
    }

    this.$triggers = this.$start.add(this.$end).add(this.$addMinutes).add(this.$subtractMinutes);
    this.$triggers.on('change', this.onTimeInputChange.bind(this));

    this.calculateValue(this.startCtrl.luxonOriginalValue, this.endCtrl.luxonOriginalValue, true);
    this.$el.data(DATA_ORIGINAL_DURATION, this.value?.current);
  }

  protected getMax(): DateTime {
    const maxInput = this.$el.attr(ATTR_MAX);
    if (maxInput === undefined) {
      return undefined
    }
    if (maxInput.toLocaleLowerCase() === 'now') {
      return DateTime.now()
    }
    return DateUtil.tryParseDateTime(maxInput)
  }

  protected onInvalid(): void {
    this.$el.removeData(DATA_VALUE).trigger('change');
  }

  /**
   * Handles change events detected from time inputs.
   */
  protected onTimeInputChange(e: Event) {
    const changeSourceCtrl = CustomComponent.getController($(e.target as HTMLElement));
    const start = this.startCtrl.luxonValue;
    const end = this.endCtrl.luxonValue;

    if (!start?.isValid || !end?.isValid) {
      return this.onInvalid();
    }

    const startChanged = changeSourceCtrl === this.startCtrl;

    // Adjust the date if the new time would put the start time after the end time.
    // For example, if the start and end were originally 12:00 PM and 2:00 PM, and then the start is changed to 3:00 PM,
    // update the date of the start time to 3:00 PM on the previous day.
    if (start > end) {
      if (!this.autoAdjust) {
        return this.onInvalid();
      }
      if (startChanged) {
        const updatedStart = start.minus(ONE_DAY)
        this.startCtrl.syncValue(updatedStart);
      } else {
        const updatedEnd = end.plus(ONE_DAY);
        this.endCtrl.syncValue(updatedEnd);
      }
      // this will trigger another change event - don't update the range value yet
      return;
    }

    if (end.diff(start) >= ONE_DAY) {
      const max = this.getMax()
      if (startChanged || (max && end > max)) {
        // correct the end date if it was previously modified due to selecting a "later" start time, or if it exceeds
        // the max date specific
        this.endCtrl.syncValue(end.minus(ONE_DAY));
      } else {
        // correct the "start" date if it was previously modified due to selecting an "earlier" end time
        this.startCtrl.syncValue(start.plus(ONE_DAY));
      }
      // this will trigger another change event - don't update the range value yet
      return;
    }

    this.calculateValue(start, end);
  }

  /**
   * Calculates the duration represented by the `start` and `end` values, and emits a
   * {@link TimeRangeComponent#:event:change} event if the values has changed.
   *
   * @fires TimeRangeComponent#change
   * @param start
   * @param end
   * @param isInitial - A flag representing whether the call is for calculating the initial value for the component. If
   *                    `true`, then the calculated duration is used as the `original` value for the generated
   *                    {@link TimeRangeDurationState}.
   */
  protected calculateValue(start: DateTime, end: DateTime, isInitial: boolean = false) {
    const previousDuration = this.value?.current;
    const extraMinutes = this.calculateExtraMins();
    const updatedDuration = start && end ? end.diff(start).plus({ minutes: extraMinutes }).shiftTo('hours', 'minutes') : undefined;
    const isChanged = updatedDuration?.valueOf() !== previousDuration?.valueOf();
    if (isChanged) {
      const originalDuration = isInitial ? updatedDuration : this.originalDuration;
      const diff = this.calculateDiff(updatedDuration, originalDuration).shiftTo('hours', 'minutes');
      const crossesMidnight = start.day !== end.day;
      const offsetChange = Duration.fromObject({ minutes: start.offset - end.offset }).as('hours');
      const value = new TimeRangeDurationState(updatedDuration, originalDuration, diff, crossesMidnight, offsetChange);
      this.$el.data(DATA_VALUE, value).trigger('change');
    }
  }

  /**
   * Calculates the difference between the update and original durations.
   */
  protected calculateDiff(updatedDuration: Duration, originalDuration: Duration): Duration {
    if (updatedDuration && originalDuration) {
      return updatedDuration.minus(originalDuration);
    }
    if (updatedDuration) {
      return updatedDuration;
    }
    if (originalDuration) {
      return originalDuration.negate();
    }

    return undefined;
  }

  protected calculateExtraMins(): number {
    return this.countExtraMinutes(this.$addMinutes) - this.countExtraMinutes(this.$subtractMinutes);
  }

  protected countExtraMinutes($el: JQuery): number {
    return $el.toArray().reduce((totalMinutes, el) => {
      const parsedValue = parseInt($(el).val() as string, 10);
      if (isNaN(parsedValue)) {
        return totalMinutes;
      }
      return totalMinutes + parsedValue;
    }, 0);
  }

}
